import { useEffect, useRef, useState } from 'react';
import { platformFeatures } from '@/mocks/homepage';

export default function PlatformSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="platform" ref={ref} className="bg-charcoal-400 py-24 md:py-36 relative overflow-hidden">
      {/* Soft glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sage-600/10 rounded-full blur-[120px]" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-16 md:mb-24 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-sage-300 text-xs tracking-[0.3em] uppercase mb-4 font-medium">
              The Ecosystem
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-white font-medium leading-tight max-w-3xl mx-auto">
              A peaceful operating system
              <br />
              <span className="italic font-light">for your financial life.</span>
            </h2>
            <p className="mt-6 text-white/60 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
              Built with intention, not algorithms. Designed to return time, not consume it.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {platformFeatures.map((feature, i) => (
              <div
                key={feature.id}
                className={`group bg-white/5 backdrop-blur-sm rounded-2xl p-7 md:p-8 border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all duration-700 ${
                  visible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${200 + i * 120}ms` }}
              >
                <div className="w-11 h-11 rounded-full bg-sage-500/20 flex items-center justify-center mb-5">
                  <i className={`${feature.icon} text-sage-300 text-lg`} />
                </div>
                <h3 className="text-white text-base md:text-lg font-medium mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* App Preview Visual */}
          <div
            className={`mt-16 md:mt-24 transition-all duration-1000 delay-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="bg-cream-50 rounded-3xl p-8 md:p-12 max-w-3xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-sage-500 text-xs tracking-[0.2em] uppercase mb-3 font-medium">
                    Coming Soon
                  </p>
                  <h3 className="font-serif text-2xl md:text-3xl text-charcoal-300 font-medium leading-snug mb-4">
                    Your stewardship,
                    <br />
                    <span className="italic font-light">at a glance.</span>
                  </h3>
                  <p className="text-charcoal-100 text-sm leading-relaxed mb-6">
                    A calm, intentional interface that shows what matters. No noise. No overwhelm. Just the visibility you need to practice well.
                  </p>
                  <div className="flex items-center gap-3 text-sage-500 text-sm">
                    <i className="ri-checkbox-circle-line" />
                    <span>Quiet notifications</span>
                  </div>
                  <div className="flex items-center gap-3 text-sage-500 text-sm mt-2">
                    <i className="ri-checkbox-circle-line" />
                    <span>Weekly rhythm prompts</span>
                  </div>
                  <div className="flex items-center gap-3 text-sage-500 text-sm mt-2">
                    <i className="ri-checkbox-circle-line" />
                    <span>Goal-aligned spending</span>
                  </div>
                </div>
                <div className="relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Minimalist%20calm%20financial%20dashboard%20UI%20design%20soft%20beige%20and%20sage%20green%20color%20palette%20clean%20typography%20gentle%20card%20layout%20peaceful%20interface%20no%20clutter%20warm%20white%20background%20subtle%20shadows%20modern%20mobile%20app%20screen%20mockup%20editorial%20style%20professional&width=500&height=600&seq=platform1&orientation=portrait"
                    alt="Calm financial dashboard preview"
                    className="w-full rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}